import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TRACK_OPTIONS } from "@/lib/school";
import { monthRange, monthOf } from "@/lib/vigilanza";
import { ImpegniKpis, KindTotals, MonthlyTrend, StudentRanking, kindLabel, kindColor } from "@/components/mansioni/stats/ImpegniStatsParts";

const sel = "mt-1.5 h-9 rounded-md border border-stone-200 bg-white px-2 text-sm block";
const academicYear = () => { const t = monthRange(0).from; const y = Number(t.slice(0, 4)) - (Number(t.slice(5, 7)) < 9 ? 1 : 0); return { from: `${y}-09-01`, to: monthOf(`${y + 1}-08-01`).to }; };
const PRESETS = [["Questo mese", () => monthRange(0)], ["Mese scorso", () => monthRange(-1)],
  ["Ultimi 3 mesi", () => ({ from: monthRange(-2).from, to: monthRange(0).to })], ["Anno accademico", academicYear]];

export default function StatisticheImpegni() {
  const [range, setRange] = useState(academicYear());
  const [f, setF] = useState({ kind: "", year_level: "", track_type: "", student_id: "" });
  const { data: kindsAll = [] } = useQuery({ queryKey: ["impegni-kinds"], queryFn: async () => (await api.get("/mansioni-area/impegni/kinds")).data });
  const { data: students = [] } = useQuery({ queryKey: ["students-list", "resid"], queryFn: async () => (await api.get("/students", { params: { track_type: "residenziale" } })).data });
  const { data, isLoading } = useQuery({
    queryKey: ["impegni-stats", range, f], enabled: !!range.from && !!range.to && range.from <= range.to,
    queryFn: async () => (await api.get("/mansioni-area/stats/impegni", { params: { since: range.from, until: range.to, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) } })).data,
  });
  const colorOf = (k) => kindColor(k, Math.max(0, kindsAll.indexOf(k)));
  const filter = (k, label, opts, testId) => (
    <div><Label className="text-xs">{label}</Label>
      <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={sel} data-testid={testId}>
        <option value="">Tutti</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select></div>
  );

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1500px] mx-auto" data-testid="stats-impegni-page">
      <PageHeader title="Statistiche impegni esterni" subtitle="Storico di visite, uscite e servizi esterni: per tipo, nel tempo e per studente."/>
      <Card className="p-4 border border-stone-200 shadow-none rounded-xl flex flex-wrap items-end gap-4 mb-6">
        <div className="flex gap-1.5 flex-wrap">
          {PRESETS.map(([l, fn]) => (
            <button key={l} onClick={() => setRange(fn())} data-testid={`imp-stats-preset-${l.replace(/\s/g, "-").toLowerCase()}`}
              className="px-3 py-1.5 rounded-full text-xs border border-stone-200 bg-white hover:border-stone-400 transition-colors">{l}</button>
          ))}
        </div>
        <div><Label className="text-xs">Dal</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1.5 w-40" data-testid="imp-stats-from"/></div>
        <div><Label className="text-xs">Al</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1.5 w-40" data-testid="imp-stats-to"/></div>
        {filter("kind", "Tipo", kindsAll.map((k) => [k, kindLabel(k)]), "imp-stats-kind")}
        {filter("year_level", "Anno", [1, 2, 3, 4, 5, 6, 7].map((y) => [y, `${y}° anno`]), "imp-stats-year")}
        {filter("track_type", "Percorso", TRACK_OPTIONS.map((t) => [t, t.charAt(0).toUpperCase() + t.slice(1)]), "imp-stats-track")}
        {filter("student_id", "Studente", students.filter((s) => (s.status || "attivo") === "attivo").sort((a, b) => `${a.last_name}`.localeCompare(`${b.last_name}`)).map((s) => [s.id, `${s.last_name} ${s.first_name}`]), "imp-stats-student")}
      </Card>

      {isLoading && <div className="text-sm text-stone-400">Calcolo…</div>}
      {data && (
        <div className="space-y-8">
          <ImpegniKpis s={data.summary}/>
          <section><h2 className="text-base md:text-lg font-semibold text-[#1C352D] mb-3">Totali per tipo</h2>
            {data.kinds.length ? <KindTotals kinds={data.kinds} colorOf={colorOf}/> : <Card className="p-8 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessun impegno nel periodo.</Card>}
          </section>
          <section><h2 className="text-base md:text-lg font-semibold text-[#1C352D] mb-3">Andamento mese per mese</h2>
            <MonthlyTrend months={data.months} kinds={data.kinds} colorOf={colorOf}/>
          </section>
          <section><h2 className="text-base md:text-lg font-semibold text-[#1C352D] mb-3">Classifica per studente</h2>
            {data.students.length ? <StudentRanking students={data.students} kinds={data.kinds} colorOf={colorOf}/> : <Card className="p-8 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessuno studente attivo con questi filtri.</Card>}
          </section>
        </div>
      )}
    </div>
  );
}
